<template>
    <div class="fixed z-[9999] overflow-y-auto overscroll-contain" :style="overlayStyle">
        <div class="absolute inset-0 bg-black/35" aria-hidden="true"></div>

        <div class="relative w-full min-h-full flex items-start justify-center p-4 md:p-6">
            <div class="w-[96%] max-w-md">
                <div
                    class="rounded-2xl overflow-hidden shadow-2xl border border-slate-200 bg-white flex flex-col min-h-0 max-h-full">
                    <div class="px-5 py-4 md:px-6 md:py-5 bg-green-50 border-b border-green-100">
                        <div class="flex items-start justify-between gap-3">
                            <div class="min-w-0">
                                <div class="flex items-center gap-2">
                                    <div
                                        class="w-10 h-10 rounded-xl flex items-center justify-center bg-green-100 border border-green-200 shadow-sm shadow-black/10">
                                        <font-awesome-icon icon="fa-solid fa-clock"
                                            class="text-green-700 text-[18px]" />
                                    </div>
                                    <div class="min-w-0">
                                        <h2
                                            class="text-lg md:text-xl font-semibold text-green-900 break-words whitespace-normal leading-snug">
                                            Historial de compras
                                        </h2>
                                        <p class="text-xs md:text-sm text-green-700 break-words whitespace-normal leading-snug"
                                            v-if="customerName">
                                            {{ customerName }}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <button @click="onClose"
                                class="text-green-700 hover:text-green-900 w-10 h-10 flex items-center justify-center hover:bg-green-100 rounded-xl active:scale-95"
                                aria-label="Cerrar">
                                <font-awesome-icon icon="fa-solid fa-circle-xmark" class="text-green-700 text-[20px]" />
                            </button>
                        </div>
                    </div>

                    <div
                        class="px-4 py-3 md:px-5 md:py-4 bg-gradient-to-b from-green-50 to-white overflow-y-auto flex-1 overscroll-contain min-h-0">
                        <div class="mb-4">
                            <div class="rounded-xl border border-green-100 bg-green-50 px-4 py-3">
                                <div class="text-sm font-semibold text-slate-900">
                                    Historial de última compra
                                </div>
                                <div class="text-xs text-slate-600 mt-1">
                                    Productos visibles siempre. Toca una compra para ver vencimiento, serie y precios.
                                </div>
                            </div>
                        </div>

                        <div v-if="loading"
                            class="bg-white/70 border border-slate-200 rounded-xl px-4 py-4 text-sm text-gray-700">
                            Cargando historial…
                        </div>

                        <div v-else-if="error"
                            class="bg-red-50 border border-red-200 rounded-xl px-4 py-4 text-sm text-red-700">
                            {{ error }}
                        </div>

                        <div v-else-if="!visibleSales || visibleSales.length === 0"
                            class="bg-white/70 border border-slate-200 rounded-xl px-4 py-4 text-sm text-gray-700">
                            Este cliente no tiene compras registradas.
                        </div>

                        <div v-else class="space-y-3">
                            <div class="space-y-2">
                                <button v-for="(sale, index) in visibleSales" :key="sale.id"
                                    class="w-full text-left rounded-xl border border-green-100 bg-white hover:bg-green-50 active:scale-[0.99] transition"
                                    @click="toggleOpen(sale.id)">
                                    <div class="px-4 py-3">
                                        <div class="flex items-start justify-between gap-3">
                                            <div class="min-w-0">
                                                <div class="text-sm font-semibold text-slate-900">
                                                    {{ index === 0 ? 'Última compra' : 'Compra' }}
                                                </div>
                                                <div class="text-xs text-slate-600 mt-1">
                                                    {{ formatDateShort(sale.fecha_de_emision) }}
                                                    <span class="mx-1">·</span>
                                                    {{ sale.tipo_pago }}
                                                    <span class="mx-1">·</span>
                                                    {{ sale.tipo_comprobante || 'Comprobante' }}
                                                </div>
                                            </div>

                                            <div class="shrink-0 text-right">
                                                <div class="text-sm font-bold text-slate-900">
                                                    {{ prefix(sale) }} {{ formatMoney(sale.total) }}
                                                </div>
                                                <div class="text-xs text-slate-500">Total</div>
                                            </div>
                                        </div>

                                        <div class="mt-3" v-if="sale.details && sale.details.length">
                                            <div class="space-y-2">
                                                <div v-for="(d, idx) in sale.details.slice(0, 3)" :key="idx"
                                                    class="flex items-start justify-between gap-3">
                                                    <div class="min-w-0">
                                                        <div class="text-sm font-semibold text-slate-800 truncate">
                                                            {{ d.nombre_producto }}
                                                        </div>
                                                        <div class="text-xs text-slate-600">
                                                            Cant.: <span class="font-semibold">{{ d.cantidad }}</span>
                                                            <span v-if="d.unidad_de_medida">· {{ d.unidad_de_medida
                                                            }}</span>
                                                        </div>
                                                    </div>
                                                    <div class="text-sm font-semibold text-slate-800 shrink-0">
                                                        {{ prefix(sale) }} {{ formatMoney(d.total) }}
                                                    </div>
                                                </div>

                                                <div v-if="sale.details.length > 3" class="text-xs text-slate-500">
                                                    +{{ sale.details.length - 3 }} producto(s) más…
                                                </div>
                                            </div>
                                        </div>

                                        <div class="mt-3 flex items-center justify-end">
                                            <div
                                                class="inline-flex items-center gap-2 text-xs font-semibold text-green-700">
                                                Detalles
                                                <font-awesome-icon
                                                    :icon="isOpen(sale.id) ? 'fa-solid fa-angle-up' : 'fa-solid fa-chevron-right'"
                                                    class="text-green-400" />
                                            </div>
                                        </div>
                                    </div>

                                    <div v-if="isOpen(sale.id)" class="border-t border-slate-200 bg-white">
                                        <div class="px-4 py-4">
                                            <div class="pr-1">
                                                <div v-for="(d, idx) in sale.details" :key="idx"
                                                    class="flex items-start justify-between gap-3 py-3 border-b border-slate-100 last:border-b-0">
                                                    <div class="min-w-0">
                                                        <div class="text-sm font-semibold text-slate-800 truncate">
                                                            {{ d.nombre_producto }}
                                                        </div>
                                                        <div class="text-xs text-slate-600 mt-1">
                                                            Cant.: <span class="font-semibold">{{ d.cantidad }}</span>
                                                            <span v-if="d.unidad_de_medida">· {{ d.unidad_de_medida
                                                            }}</span>
                                                            <span v-if="d.product_serie">· Serie: {{ d.product_serie
                                                            }}</span>
                                                        </div>
                                                        <div v-if="d.vencimiento"
                                                            class="text-[11px] mt-2 inline-flex items-center gap-2 px-2 py-1 rounded-lg bg-amber-50 text-amber-800 border border-amber-200">
                                                            <font-awesome-icon
                                                                icon="fa-solid fa-triangle-exclamation" />
                                                            Vence: {{ formatDateShort(d.vencimiento) }}
                                                        </div>
                                                    </div>

                                                    <div class="shrink-0 text-right">
                                                        <div class="text-sm font-bold text-slate-800">
                                                            {{ prefix(sale) }} {{ formatMoney(d.total) }}
                                                        </div>
                                                        <div class="text-xs text-slate-500">
                                                            Unit: {{ prefix(sale) }} {{ formatMoney(d.precio_de_venta)
                                                            }}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="px-5 py-4 md:px-6 bg-green-50 border-t border-green-100">
                        <div class="flex justify-end gap-2">
                            <button
                                class="px-4 py-2 rounded-xl text-sm font-semibold bg-green-600 text-white hover:bg-green-700 active:scale-95"
                                @click="onClose">
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

export default {
    name: "CustomerPurchaseHistoryModal",
    props: {
        onClose: {
            type: Function,
            required: true,
        },
        containerEl: {
            type: Object,
            default: null,
        },
        customerName: {
            type: String,
            default: null,
        },
        sales: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        error: {
            type: String,
            default: null,
        },
    },
    setup(props) {
        const visibleSales = computed(() => {
            const list = Array.isArray(props.sales) ? props.sales : [];
            return list.slice(0, 3);
        });

        const overlayStyle = ref({
            top: "0px",
            left: "0px",
            width: "100vw",
            height: "100vh",
        });

        let resizeObserver = null;
        let rafId = null;

        const computeOverlayRect = () => {
            const el = props.containerEl;

            if (!el || typeof el.getBoundingClientRect !== "function" || typeof window === "undefined") {
                overlayStyle.value = {
                    top: "0px",
                    left: "0px",
                    width: "100vw",
                    height: "100vh",
                };
                return;
            }

            const rect = el.getBoundingClientRect();
            const top = Math.max(rect.top, 0);
            const left = Math.max(rect.left, 0);
            const right = Math.min(rect.right, window.innerWidth);
            const bottom = Math.min(rect.bottom, window.innerHeight);
            const width = Math.max(0, right - left);
            const height = Math.max(0, bottom - top);

            overlayStyle.value = {
                top: `${top}px`,
                left: `${left}px`,
                width: `${width}px`,
                height: `${height}px`,
            };
        };

        const scheduleComputeOverlayRect = () => {
            if (typeof window === "undefined") return;
            if (rafId) return;
            rafId = window.requestAnimationFrame(() => {
                rafId = null;
                computeOverlayRect();
            });
        };

        const attachObservers = () => {
            if (typeof window === "undefined") return;

            window.addEventListener("resize", scheduleComputeOverlayRect, { passive: true });
            window.addEventListener("scroll", scheduleComputeOverlayRect, true);

            const el = props.containerEl;
            if (el && typeof window.ResizeObserver !== "undefined") {
                resizeObserver = new window.ResizeObserver(scheduleComputeOverlayRect);
                resizeObserver.observe(el);
            }
        };

        const detachObservers = () => {
            if (typeof window === "undefined") return;

            window.removeEventListener("resize", scheduleComputeOverlayRect);
            window.removeEventListener("scroll", scheduleComputeOverlayRect, true);

            if (resizeObserver) {
                resizeObserver.disconnect();
                resizeObserver = null;
            }
            if (rafId) {
                window.cancelAnimationFrame(rafId);
                rafId = null;
            }
        };

        watch(
            () => props.containerEl,
            () => {
                detachObservers();
                computeOverlayRect();
                attachObservers();
            }
        );

        onMounted(() => {
            computeOverlayRect();
            attachObservers();
        });

        onUnmounted(() => {
            detachObservers();
        });

        const openIds = ref(new Set());

        const toggleOpen = (id) => {
            const next = new Set(openIds.value);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            openIds.value = next;
        };

        const isOpen = (id) => openIds.value.has(id);

        const prefix = (sale) => sale?.prefijo_moneda ?? "";

        const formatMoney = (value) => {
            const number = Number(value ?? 0);
            try {
                return number.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                });
            } catch (_) {
                return String(number);
            }
        };

        const formatDateShort = (value) => {
            if (!value) return "";
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return String(value);
            try {
                return date.toLocaleDateString() + " " + date.toLocaleTimeString();
            } catch (_) {
                return date.toISOString();
            }
        };

        return {
            visibleSales,
            overlayStyle,
            toggleOpen,
            isOpen,
            prefix,
            formatMoney,
            formatDateShort,
            props,
        };
    },
};
</script>
